/**
 * Attempts to fetch the raw content of variables.tf from a public GitHub repository.
 */
export const fetchGithubVariables = async (repoUrl: string): Promise<string> => {
  // 1. Clean URL and parse parts
  // Expected formats:
  // https://github.com/org/repo
  // https://github.com/org/repo/tree/branch/path

  let rawUrl = '';
  const cleanUrl = repoUrl.replace(/\/$/, '');

  if (cleanUrl.includes('github.com')) {
    // Convert github.com URL to raw.githubusercontent.com
    // Simple heuristic: if no 'tree' or 'blob', assume main/master at root

    const parts = cleanUrl.split('github.com/')[1].split('/');
    const owner = parts[0];
    const repo = parts[1];

    // Simple detection of path
    let branch = 'main'; // default
    let path = '';

    if (parts.includes('tree') || parts.includes('blob')) {
      const typeIndex = parts.findIndex((p) => p === 'tree' || p === 'blob');
      branch = parts[typeIndex + 1];
      path = parts.slice(typeIndex + 2).join('/');
    }

    // Construct raw URL for variables.tf
    // If path is empty, it's root
    const pathSegment = path ? `/${path}` : '';
    // Try 'main' first
    rawUrl = `https://raw.githubusercontent.com/${owner}/${repo}/${branch}${pathSegment}/variables.tf`;
  } else {
    throw new Error('Invalid GitHub URL. Please use a valid repository link.');
  }

  // Helper for fetch with timeout
  const fetchWithTimeout = async (url: string, ms = 5000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), ms);
    try {
      const response = await fetch(url, { signal: controller.signal });
      clearTimeout(id);
      return response;
    } catch (err) {
      clearTimeout(id);
      throw err;
    }
  };

  try {
    const response = await fetchWithTimeout(rawUrl);

    if (!response.ok) {
      // Fallback: Try 'master' branch if 'main' failed and branch wasn't explicit
      if (rawUrl.includes('/main/')) {
        const masterUrl = rawUrl.replace('/main/', '/master/');
        const respMaster = await fetchWithTimeout(masterUrl);
        if (respMaster.ok) {
          return await respMaster.text();
        }
      }

      if (response.status === 404) {
        throw new Error(
          "The 'variables.tf' file was not found in the repository. Please verify the path."
        );
      }

      throw new Error(`Failed to fetch variables.tf: ${response.statusText} (${response.status})`);
    }

    return await response.text();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Fetch Error:', error);
    // Propagate specific errors that we created above
    if (
      error.message.includes('not found') ||
      error.message.includes('Invalid GitHub URL') ||
      error.message.includes('Failed to fetch')
    ) {
      throw error;
    }
    if (error.name === 'AbortError') {
      throw new Error('Connection timed out while fetching from GitHub.');
    }
    throw new Error(
      'Could not auto-fetch variables.tf. Ensure the repo is public and the URL is correct.'
    );
  }
};
